//
// Copyright (c) 2011-2024 by Curalate, Inc
//

import React, { useState } from 'react';

import { IContentLibrarySourcesOptions } from '@apis/content-library/contentLibraryApiModel';
import HR from '@components/atoms/hr/HR';
import { CaptionErrorText } from '@components/atoms/text/Text';
import { ChipSet } from '@components/media-filters/ChipSet';
import { CollapsibleSection } from '@components/media-filters/CollapsibleSection';
import { FilterSection } from '@components/media-filters/filterSectionModels';
import {
  MediaLibrarySearchTagHeaders,
  MediaLibrarySearchTagPrefixes,
} from '@components/media-filters/mediaFilterConstants';
import { MediaFilterTrackingIds } from '@components/media-filters/mediaFilterTrackingIds';
import { useAutocompleteUsernamesHashtagsQuery } from '@components/media-filters/useAutocompleteUsernamesHashtagsQuery';
import { SearchBarWithTypeAhead } from '@components/molecules/search-bar-with-type-ahead/SearchBarWithTypeAhead';

type SearchResult = React.ComponentProps<
  typeof SearchBarWithTypeAhead
>['typeAheadResults'][number];

export const DEFAULT_HASHTAGS_FILTER_SECTION: FilterSection<string> = {
  options: [],
  paramKey: 'igHashtags',
};

export const DEFAULT_USERNAMES_FILTER_SECTION: FilterSection<string> = {
  options: [],
  paramKey: 'igUsernames',
};

export interface UsernamesHashtagsFilterProps {
  hashtagsSection: FilterSection<string> | undefined;
  isInitiallyCollapsed?: boolean;
  onChange: (section: FilterSection<string>) => void;
  sources: IContentLibrarySourcesOptions;
  usernamesSection: FilterSection<string> | undefined;
}

export const UsernamesHashtagsFilter = ({
  hashtagsSection = DEFAULT_HASHTAGS_FILTER_SECTION,
  isInitiallyCollapsed,
  onChange,
  sources,
  usernamesSection = DEFAULT_USERNAMES_FILTER_SECTION,
}: UsernamesHashtagsFilterProps) => {
  const [searchTerm, setSearchTerm] = useState<string>('');

  const { data, isError, isFetching } = useAutocompleteUsernamesHashtagsQuery(
    searchTerm,
    sources,
  );

  const { hashtags = [], usernames = [] } = data ?? {};

  const selectedHashtags = hashtagsSection.options.filter(
    ({ isSelected }) => isSelected,
  );
  const selectedUsernames = usernamesSection.options.filter(
    ({ isSelected }) => isSelected,
  );
  const selectedOptions = [...selectedHashtags, ...selectedUsernames];

  const handleSelect = async ({ header, label, prefix }: SearchResult) => {
    const option = {
      isSelected: true,
      label: prefix + label,
      value: label,
    };

    switch (header) {
      case MediaLibrarySearchTagHeaders.igHashtags: {
        if (selectedHashtags.some(({ value }) => value === label)) {
          return;
        }

        onChange({
          ...hashtagsSection,
          options: [...hashtagsSection.options, option],
        });
        break;
      }
      case MediaLibrarySearchTagHeaders.igUsernames: {
        if (selectedUsernames.some(({ value }) => value === label)) {
          return;
        }

        onChange({
          ...usernamesSection,
          options: [...usernamesSection.options, option],
        });
        break;
      }
    }
  };

  return (
    <>
      <CollapsibleSection
        isCollapsed={isInitiallyCollapsed}
        title={'Usernames & Hashtags'}
      >
        <SearchBarWithTypeAhead
          ellipsize={1}
          isLoadingTypeAheadResults={isFetching}
          getTypeAheadResults={async (searchTerm: string | undefined) =>
            setSearchTerm(searchTerm ?? '')
          }
          onSearchSelection={handleSelect}
          resultsLabel='Posts'
          searchBarPlaceholderText={`${MediaLibrarySearchTagPrefixes.igHashtags}hashtags and ${MediaLibrarySearchTagPrefixes.igUsernames}usernames`}
          searchPrefixes={[
            MediaLibrarySearchTagPrefixes.igHashtags,
            MediaLibrarySearchTagPrefixes.igUsernames,
          ]}
          shouldEnableSearchTermFiltering={false}
          trackingIdPrefix={
            MediaFilterTrackingIds.USERNAMES_HASHTAGS_FILTER_SEARCH_BAR
          }
          typeAheadResults={[...hashtags, ...usernames]}
        />

        {isError && (
          <CaptionErrorText>
            An error occurred retrieving your tracked usernames and hashtags.
            Please try again or contact Bazaarvoice Support.
          </CaptionErrorText>
        )}

        {selectedOptions.length > 0 && (
          <ChipSet
            options={selectedOptions}
            onClick={(option) => {
              option.isSelected = false;
              onChange(hashtagsSection);
              onChange(usernamesSection);
            }}
            trackingId={
              MediaFilterTrackingIds.USERNAMES_HASHTAGS_FILTER_CHIP_SET
            }
          />
        )}
      </CollapsibleSection>
      <HR />
    </>
  );
};
